import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { EyeIcon, EyeOff } from 'lucide-react';

import { Button } from '@/slw/shared/ui/button';
import FormProvider, { Input, Checkbox } from '@/slw/shared/ui/form';
import { Label } from '@/slw/shared/ui/label';
import { cn } from '@/slw/shared/lib/utils';

const schema = z.object({
  password: z.string().min(1, 'Password required'),
  isRemembered: z.boolean(),
});

export function PasswordStep({
  email,
  onSubmit,
  loading,
  onForgot,
}: {
  email: string;
  onSubmit: (password: string, isRemembered: boolean) => void;
  loading: boolean;
  onForgot: () => void;
}) {
  const [show, setShow] = useState(false);
  const methods = useForm<{ password: string; isRemembered: boolean }>({
    resolver: zodResolver(schema),
    defaultValues: {
      password: '',
      isRemembered: false,
    },
  });

  const errorPassword = methods.formState.errors?.password;
  const isRemembered = methods.watch('isRemembered');

  const handleSubmit = (data: { password: string; isRemembered: boolean }) => {
    onSubmit(data.password, data.isRemembered);
  };

  return (
    <FormProvider {...methods} onSubmit={handleSubmit} className='w-full flex flex-col justify-center'>
      <p className='font-normal text-sm mb-[2rem]'>Your email address: {email}</p>
      <div className='flex flex-col gap-2'>
        <Label htmlFor='password' className='whitespace-nowrap font-normal text-sm'>
          Your password<span className='text-[#dc2626]'>*</span>
        </Label>
        <div className='relative'>
          <Input
            direction='column'
            id='password'
            name='password'
            type={show ? 'text' : 'password'}
            placeholder='Enter your password'
          />
          <button
            type='button'
            className={cn(
              'absolute top-1/2  ltr:right-4 rtl:left-4 cursor-pointer',
              errorPassword ? '-translate-y-[calc(50%_+_0.7rem)]' : '-translate-y-1/2',
            )}
            onClick={() => setShow((v) => !v)}
            aria-label={show ? 'Hide password' : 'Show password'}
          >
            {show ? <EyeIcon className='w-5 h-5 text-default-400' /> : <EyeOff className='w-5 h-5 text-default-400' />}
          </button>
        </div>
        
        {/* Remember Password Checkbox */}
        <div className='flex items-center space-x-2 mt-2'>
          <Checkbox
            id='isRemembered'
            name='isRemembered'
            checked={isRemembered}
          />
          <Label htmlFor='isRemembered' className='text-sm text-gray-600 cursor-pointer'>
            Remember Password
          </Label>
        </div>
        
        <button
          type='button'
          className='text-[#c8a978] text-sm leading-[150%] flex justify-end mb-8 cursor-pointer hover:underline'
          onClick={onForgot}
        >
          Forgot Password?
        </button>
      </div>
      <Button type='submit' disabled={loading} className='w-full' style={{ marginTop: 0 }}>
        {loading ? 'Loading...' : 'LOGIN'}
      </Button>
    </FormProvider>
  );
}
